import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useAuth } from '@/auth/AuthProvider';
import { api, ApiError } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';

interface FormValues {
  organization_name: string;
  email: string;
  password: string;
  name?: string;
}

// Turnstile script + render. Cloudflare's bot challenge widget. If no
// sitekey is configured server-side, the widget is skipped and the
// form submits without a token (server also skips verification).
declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement | string,
        opts: { sitekey: string; callback: (token: string) => void; 'expired-callback'?: () => void },
      ) => string;
      reset: (id?: string) => void;
    };
  }
}

function useTurnstile(sitekey: string | null) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [token, setToken] = useState<string>('');
  const widgetId = useRef<string | null>(null);

  useEffect(() => {
    if (!sitekey) return;
    const SCRIPT_ID = 'cf-turnstile-script';
    const onReady = () => {
      if (!containerRef.current || !window.turnstile) return;
      if (widgetId.current) window.turnstile.reset(widgetId.current);
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey,
        callback: (t) => setToken(t),
        'expired-callback': () => setToken(''),
      });
    };
    if (document.getElementById(SCRIPT_ID)) {
      onReady();
      return;
    }
    const s = document.createElement('script');
    s.id = SCRIPT_ID;
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    s.async = true;
    s.defer = true;
    s.onload = onReady;
    document.head.appendChild(s);
  }, [sitekey]);

  return { containerRef, token };
}

// Public signup: anyone can create a workspace and become its admin.
// On success we immediately log them in via the existing login flow so
// they land in the dashboard ready to pair their first WhatsApp number.
export function SignupPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { login } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [siteKey, setSiteKey] = useState<string | null>(null);
  const { register, handleSubmit, formState: { isSubmitting } } = useForm<FormValues>();
  const turnstile = useTurnstile(siteKey);

  // Load the Turnstile sitekey once. If the server returns null, the
  // widget never mounts and we submit without a token.
  useEffect(() => {
    api
      .get<{ turnstile_site_key: string | null }>('/api/auth/config')
      .then((c) => setSiteKey(c.turnstile_site_key))
      .catch(() => setSiteKey(null));
  }, []);

  async function onSubmit(values: FormValues) {
    setError(null);
    if (siteKey && !turnstile.token) {
      setError(t('signup.captcha_required', { defaultValue: 'Please complete the security check.' }));
      return;
    }
    const email = values.email.trim().toLowerCase();
    try {
      await api.post('/api/organizations/signup', {
        organization_name: values.organization_name.trim(),
        email,
        password: values.password,
        name: values.name?.trim() || undefined,
        turnstile_token: turnstile.token || undefined,
      });
      await login(email, values.password);
      navigate('/dashboard');
    } catch (e) {
      if (e instanceof ApiError && e.body && (e.body as any).error === 'email_taken') {
        setError(t('signup.email_taken', { defaultValue: 'That email is already in use.' }));
      } else if (e instanceof ApiError && (e.body as any)?.error === 'captcha_failed') {
        setError(t('signup.captcha_failed', { defaultValue: 'Security check failed. Refresh and try again.' }));
      } else if (e instanceof ApiError && e.status === 429) {
        setError(
          t('signup.rate_limited', {
            defaultValue: 'Too many signup attempts from your network. Try again in an hour.',
          }),
        );
      } else {
        setError(
          t('signup.error', { defaultValue: 'Could not create workspace. Try a different email or contact support.' }),
        );
      }
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4">
      <div className="absolute top-4 end-4">
        <LanguageSwitcher />
      </div>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 brand-gradient h-12 w-12 rounded-xl" />
          <CardTitle className="text-2xl">
            {t('signup.title', { defaultValue: 'Create your workspace' })}
          </CardTitle>
          <CardDescription>
            {t('signup.subtitle', {
              defaultValue: 'Start in seconds. You become the admin of your own workspace.',
            })}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="organization_name">
                {t('signup.organization_name', { defaultValue: 'Company / workspace name' })}
              </Label>
              <Input
                id="organization_name"
                required
                minLength={2}
                maxLength={80}
                placeholder={t('signup.organization_placeholder', { defaultValue: 'Acme Clinic' })}
                {...register('organization_name', { required: true })}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="name">{t('signup.your_name', { defaultValue: 'Your name' })}</Label>
              <Input id="name" autoComplete="name" {...register('name')} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="email">{t('auth.email')}</Label>
              <Input id="email" type="email" autoComplete="email" required {...register('email', { required: true })} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">{t('auth.password')}</Label>
              <Input
                id="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
                {...register('password', { required: true, minLength: 8 })}
              />
              <p className="text-xs text-muted-foreground">
                {t('signup.password_hint', { defaultValue: 'At least 8 characters.' })}
              </p>
            </div>
            {siteKey && (
              <div ref={turnstile.containerRef} className="flex justify-center" />
            )}
            {error && (
              <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
                {error}
              </div>
            )}
            <Button type="submit" className="w-full" disabled={isSubmitting}>
              {isSubmitting
                ? t('signup.creating', { defaultValue: 'Creating workspace…' })
                : t('signup.cta', { defaultValue: 'Create workspace' })}
            </Button>
            <p className="text-xs text-center text-muted-foreground pt-2">
              {t('signup.have_account', { defaultValue: 'Already have an account?' })}{' '}
              <Link to="/login" className="text-primary hover:underline font-medium">
                {t('signup.signin_link', { defaultValue: 'Sign in' })}
              </Link>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
